import { rem } from 'polished'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useInView } from 'react-intersection-observer'
import styled from 'styled-components'

import MessageBanner from 'LuxKit/Banners/MessageBanner'
import SmallToggleButton from 'LuxKit/Button/SmallToggleButton'
import LineArrowDownIcon from 'LuxKit/Icons/line/LineArrowDownIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import * as Analytics from 'analytics/analytics'
import AccommodationRoomTile from 'components/AccommodationKit/AccommodationRoomTile'
import type { AccommodationRoomTileActionsState } from 'components/AccommodationKit/AccommodationRoomTileActions'
import AccommodationRoomTileActions from 'components/AccommodationKit/AccommodationRoomTileActions'
import AccommodationRoomTileDetails from 'components/AccommodationKit/AccommodationRoomTileDetails'
import AccommodationRoomTileImage from 'components/AccommodationKit/AccommodationRoomTileImage'
import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Pane from 'components/Common/Pane'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import { OfferPageActions, OfferPageFilterType, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import useToggle from 'hooks/useToggle'
import { take } from 'lib/array/arrayUtils'
import { hasSelectedRooms, preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import uuidV4 from 'lib/string/uuidV4Utils'
import {
  arePropertyRatesLoading,
  getPropertyRatesKey,
  isPropertyAvailableForPurchase,
} from 'selectors/offerPage/offerPageSelectors'

import PropertyAnytimeRateOption from './PropertyAnytimeRateOption'
import PropertyRateOption from './PropertyRateOption'
import PropertyRateOptionMerged from './PropertyRateOptionMerged'
import useGroupedPropertyRatesByRefundability from './useGroupedPropertyRatesByRefundability'

const RateSection = styled.div`
  > * {
    border-top: 1px solid ${props => props.theme.palette.neutral.default.five};
  }
`

const ShowMoreContent = styled(BodyText)`
  padding: ${rem(12)};
`

const MAX_RATES_TO_SHOW = 100

interface Props {
  property: App.Property.Property
  offer: App.HotelOffer | undefined
  propertyRoom: App.Property.Room
  availableRooms: number | undefined
  rates: Array<App.PropertyRate>
  roomFitsCapacity: boolean
  datesAvailable: boolean
  checkIn?: string
  rooms: Array<OfferPageRoom>
  isSelected: boolean
  isHighlighted?: boolean
  roomRateUnmatched?: boolean
  onViewPackageOptions: () => void
  airport?: App.Airport
  roomListId: string
  roomPosition: number
  availableForPurchase?: boolean
  areAnytimeRates: boolean
  isLoading: boolean
}

function PropertyRoomOption(props: Props) {
  const {
    property,
    offer,
    propertyRoom,
    availableRooms = 0,
    rates,
    roomFitsCapacity,
    datesAvailable,
    checkIn,
    rooms,
    isSelected,
    isHighlighted = false,
    roomRateUnmatched = true,
    onViewPackageOptions,
    roomListId,
    roomPosition,
    availableForPurchase,
    areAnytimeRates,
    isLoading,
  } = props

  /************************************************************************************************/
  /* STOP! Do not add any more hooks for deriving packages/rooms or options/rates to this         */
  /* component! This logic is being refactored out into selectors! If you _need_ to add new       */
  /* logic, speak to Xiao.                                                                        */
  /************************************************************************************************/

  // NB: `0`s become `undefined` so we can easily conditionally render `<BookingsCountLabel/>` later.
  const bookingsCount = propertyRoom.stats.booked || undefined
  const [showingMoreRates, showMoreRates] = useToggle()
  const offerPageDispatch = useOfferPageDispatch()
  const promoteRoom = isHighlighted || isSelected
  const promoteRoomWithSpecificRate = !isSelected && isHighlighted && !roomRateUnmatched
  const promoteRoomWithSomeRates = (isSelected || (isHighlighted && roomRateUnmatched)) && !!rates.length
  const promoteRoomWithoutRates = (isSelected || (isHighlighted && roomRateUnmatched)) && !rates.length
  const initialRatesToShow = promoteRoomWithSpecificRate ? 1 : 3

  useEffect(() => {
    if (isHighlighted) {
      onViewPackageOptions()
    }
  }, [isHighlighted]) // eslint-disable-line react-hooks/exhaustive-deps

  const checkAvailability = !checkIn || !hasSelectedRooms(rooms)

  const actionsState = useMemo<AccommodationRoomTileActionsState | undefined>(() => {
    if (isLoading) return undefined
    if (!availableForPurchase) return 'sold-out'

    if (availableForPurchase) {
      const anytimeRatesActionState = areAnytimeRates ? 'available-on-dates-variant' : 'available-on-dates'
      if (offer && !offer.property?.useDynamicOccupancy) {
        if (!datesAvailable) return 'sold-out-on-dates'
        if (!roomFitsCapacity && datesAvailable) return 'exceeded-capacity'
        if (checkAvailability) {
          if (roomFitsCapacity && datesAvailable) return anytimeRatesActionState
          if (!roomFitsCapacity && datesAvailable) return 'exceeded-capacity'
        }
      }
      if (!checkIn) return anytimeRatesActionState
      if (!datesAvailable) return 'sold-out-on-dates-or-exceeded-capacity'
    }
  }, [areAnytimeRates, availableForPurchase, checkAvailability, checkIn, datesAvailable, isLoading, offer, roomFitsCapacity])

  const toggleFilter = useCallback((filter: OfferPageFilterType) => {
    offerPageDispatch({ type: OfferPageActions.setPropertyRoomId, roomId: propertyRoom.id })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter })
  }, [offerPageDispatch, propertyRoom.id])

  const handleChangeDatesClick = useCallback(() => {
    toggleFilter('checkin')
  }, [toggleFilter])

  const handleCheckAvailabilityClick = useCallback(() => {
    // If dates aren't set, the CTA will be "Select dates", so we'll use the default occupancy and
    // skip directly to selecting dates.
    toggleFilter((areAnytimeRates || hasSelectedRooms(rooms)) ? 'checkin' : 'capacity')
  }, [areAnytimeRates, rooms, toggleFilter])

  const onChangeOccupancyClick = useCallback(() => {
    toggleFilter('capacity')
  }, [toggleFilter])

  const rateListId = useMemo(() => uuidV4(),
  // eslint-disable-next-line react-hooks/exhaustive-deps
    [rates]) // regenerate id if packages change

  // Merged package options are enabled on desktop to align with the original test device targeting.
  const isDesktop = useScreenSizeUp('desktop')
  const isMergedPackageOptionsEnabled = isDesktop

  // Group property rates by refundability
  const groupedPropertyRates = useGroupedPropertyRatesByRefundability(rates, isMergedPackageOptionsEnabled)

  // Track viewing packages section
  const { ref: packagesViewRef, inView: packagesInView } = useInView({
    threshold: 0.1, // Trigger when 10% visible
    triggerOnce: true, // Only trigger once
  })

  useEffect(() => {
    if (packagesInView) {
      Analytics.trackClientEvent({
        subject: 'packages-section',
        action: 'view',
        category: 'engagement',
        type: 'operational',
        optimizelyEventId: '4971739877212160',
        optimizelyEventKey: 'view_packages_section',
      })
    }
  }, [packagesInView])

  return (
    <Pane border="neutral-five" borderRadius="S" data-testid="package-option">
      {promoteRoom && <MessageBanner kind="promotional-primary" icon={<LineArrowDownIcon />}>
        {promoteRoomWithSpecificRate && <b>You were interested in this option.</b>}
        {promoteRoomWithSomeRates && <b>You were interested in this room. Book now to secure your room!</b>}
        {promoteRoomWithoutRates && <b>You were interested in this room.</b>}
      </MessageBanner>}
      <ScrollAnchor id={propertyRoom.id} vOffset={rem(80)} />
      <AccommodationRoomTile roomPackage={propertyRoom}>
        <AccommodationRoomTileImage roomPackage={propertyRoom} />
        <AccommodationRoomTileDetails
          roomPackage={propertyRoom}
          urgencyLabels={<>
            {bookingsCount && <BookingsCountLabel bookings={bookingsCount} threshold="room" />}
            {checkIn && <RoomsRemainingLabel roomCount={availableRooms} threshold="rate" />}
          </>}
        >
          <AccommodationRoomTileActions
            roomPackage={propertyRoom}
            state={actionsState}
            isWalled={false}
            shouldShowAmenitiesAndPoliciesCta={false}
            onChangeOccupancyClick={onChangeOccupancyClick}
            onChangeDatesClick={handleChangeDatesClick}
            onCheckAvailabilityClick={handleCheckAvailabilityClick}
          />
        </AccommodationRoomTileDetails>
      </AccommodationRoomTile>
      <RateSection data-testid="rate-options">
        {propertyRoom && areAnytimeRates && (
          <PropertyAnytimeRateOption propertyRoom={propertyRoom} />
        )}

        {/* Variant: Property rates grouped by board code and inclusions */}
        {isMergedPackageOptionsEnabled &&
          take(
            groupedPropertyRates || [],
            showingMoreRates ? MAX_RATES_TO_SHOW : initialRatesToShow,
          ).map((group, index) => {
            return (
              <PropertyRateOptionMerged
                key={group.groupingKey}
                lpcOffer={offer}
                rates={group.rates}
                optionNumber={index + 1}
                showOptionLabel={groupedPropertyRates.length > 1}
                roomListId={roomListId}
                roomPosition={roomPosition}
                rateListId={rateListId}
                propertyTimezone={property.location.timezone}
                ref={index === 0 ? packagesViewRef : undefined}
              />
            )
          })}

        {/* Control: Separate rate options */}
        {!isMergedPackageOptionsEnabled && take(rates, showingMoreRates ? MAX_RATES_TO_SHOW : initialRatesToShow).map((rate, index) => {
          return <PropertyRateOption
            key={rate.rateId}
            lpcOffer={offer}
            rate={rate}
            optionNumber={index + 1}
            showOptionLabel={rates.length > 1}
            roomListId={roomListId}
            roomPosition={roomPosition}
            rateListId={rateListId}
          />
        })}

        {isLoading && <LoadingIndicator inline />}

        {/* Show more/less button for merged mode */}
        {isMergedPackageOptionsEnabled && (groupedPropertyRates?.length || 0) > initialRatesToShow && (
          <ShowMoreContent variant="medium" align="center">
            <SmallToggleButton
              on={showingMoreRates}
              onClick={showMoreRates}
            >
              {showingMoreRates ? 'Show less options' : 'Show more options'}
            </SmallToggleButton>
          </ShowMoreContent>
        )}

        {/* Show more/less button for control mode */}
        {!isMergedPackageOptionsEnabled && rates.length > initialRatesToShow && (
          <ShowMoreContent variant="medium" align="center">
            <SmallToggleButton
              on={showingMoreRates}
              onClick={showMoreRates}
            >
              {showingMoreRates ? 'Show less options' : 'Show more options'}
            </SmallToggleButton>
          </ShowMoreContent>
        )}
      </RateSection>
    </Pane>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  rooms: state.rooms,
  checkIn: state.checkInDate,
  airport: state.airport,
  availableForPurchase: isPropertyAvailableForPurchase(state, appState),
  areAnytimeRates: !getPropertyRatesKey(state, appState),
  isLoading: arePropertyRatesLoading(state, appState),
  open: !!state.activeFilter,
}), PropertyRoomOption, preventRenderWithOpenFilter)
